import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BookOpen, Calendar, Check, Copy, Download, Leaf, Lock, PawPrint, Radio, ShieldCheck, Snowflake, Sparkles, Sun, Target, Wind, Zap, X } from "lucide-react";

const ARSENAL_KEY = "titan-alpha";
const SESSION_STORAGE_KEY = "arsenal.unlocked.v1";
const BASE = "https://wingman-academy.preview.emergentagent.com";
const CUSTOMER_ASSETS = "https://customer-assets.emergentagent.com/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments";

const AMANDA_VIDEO_URL = `${CUSTOMER_ASSETS}/9da22c6dcc8147a8a43e6703782751af_amanda_commercial_final.mp4`;
const NOW_OS_HYPE_URL = `${CUSTOMER_ASSETS}/c84736183e414b50b455c8f0b1b6b487_now_os_launch_hype_15s.mp4`;
const COST_SLAYER_VIDEO_URL = `${CUSTOMER_ASSETS}/87206b83199b41ea943a8b866f0dcb9c_now_os_cost_slayer_30s.mp4`;
const FREEDOM_BRIDGE_URL = `${CUSTOMER_ASSETS}/eeeaeb746e894292ab39f97a33859d90_now_os_freedom_bridge_60s.mp4`;

// Ordered list backing the Video Arsenal section. Each entry becomes one row
// with a Copy URL + Download button pair.
const VIDEO_ASSETS = [
  { id: "amanda", label: "Amanda Commercial", filename: "amanda-commercial.mp4", url: AMANDA_VIDEO_URL },
  { id: "now-os-hype", label: "NOW OS · Hype (15s)", filename: "now-os-hype-15s.mp4", url: NOW_OS_HYPE_URL },
  { id: "now-os-cost-slayer", label: "NOW OS · Cost Slayer (30s)", filename: "now-os-cost-slayer-30s.mp4", url: COST_SLAYER_VIDEO_URL },
  { id: "now-os-freedom-bridge", label: "NOW OS · Freedom Bridge (60s)", filename: "now-os-freedom-bridge-60s.mp4", url: FREEDOM_BRIDGE_URL },
];

const STRIKE_PACKETS = [
  {
    id: "mc",
    label: "MC Packet",
    subtitle: "Motorcycle · Powersports",
    accent: "#F97316",
    variant: "mc",
    pitch: (shopName, demoLink) =>
      `Hey ${shopName}, I built something for shops like yours. Take a look — 60 seconds, no pitch: ${demoLink}`,
  },
  {
    id: "auto",
    label: "Auto Packet",
    subtitle: "Automotive · Trades",
    accent: "#60A5FA",
    variant: "work",
    pitch: (shopName, demoLink) =>
      `Hey ${shopName}, check out what I put together for your team. 60 seconds: ${demoLink}`,
  },
  {
    id: "suit",
    label: "High-End / Suit Packet",
    subtitle: "Professional Services",
    accent: "#A78BFA",
    variant: "suit",
    pitch: (shopName, demoLink) =>
      `Hey ${shopName}, I created a quick preview for your brand. Take a look: ${demoLink}`,
  },
  {
    id: "campground",
    label: "Campground Packet",
    subtitle: "Campground · RV Park",
    accent: "#22C55E",
    variant: "campground",
    pitch: (shopName, demoLink) =>
      `Hey ${shopName}, put together a quick preview of what the guest-facing side could look like for you. 60 seconds around the fire: ${demoLink}`,
  },
];

const HOOKS = [
  {
    id: "hook-1",
    text: "What if your next customer walked in because a robot sent them?",
  },
  {
    id: "hook-2",
    text: "Your competitor just automated their follow-up. You're still texting back manually.",
  },
  {
    id: "hook-3",
    text: "I don't sell ads. I build systems that make ads unnecessary.",
  },
];

// Bentley's Adventure Club · seasonal camouflage previews for the retainer.
const SEASON_CONFIG = {
  summer: {
    label: "Summer",
    icon: Sun,
    accent: "#F59E0B",
    palette: "Warm gold sunrise · dockside greens · firefly amber",
    story: "Bentley + the Firefly Regatta",
    graphic: "Sunburst circuit motif · lifeguard-yellow accents",
    campaign: "Bologna Express weekend blast + amphitheater movie push",
  },
  fall: {
    label: "Fall",
    icon: Leaf,
    accent: "#B45309",
    palette: "Burnt orange · slate river · rust foliage",
    story: "Bentley Hunts the Great Sycamore Leaf",
    graphic: "Falling-leaf badge · woodgrain overlays",
    campaign: "Hog Roast harvest weekend · scarecrow contest",
  },
  winter: {
    label: "Winter",
    icon: Snowflake,
    accent: "#60A5FA",
    palette: "Ice blue · silver river · powder-white steam",
    story: "Bentley and the Quiet Season",
    graphic: "Snowflake filigree · frosted-glass panels",
    campaign: "Off-season storytime series · deposit-drop early-bird",
  },
  spring: {
    label: "Spring",
    icon: Wind,
    accent: "#22C55E",
    palette: "Fresh moss · dogwood pink · morning-fog cream",
    story: "Bentley Wakes the River",
    graphic: "New-leaf sprout mark · pastel wash borders",
    campaign: "Site-reservation kickoff · Mother's Day family pack",
  },
};

const SEASON_ORDER = ["spring", "summer", "fall", "winter"];

const ADVENTURE_CLUB_PILLARS = [
  {
    icon: BookOpen,
    label: "Monthly Story Chapter",
    detail: "New 3-page Bentley legend page pushed to /bentley on the 1st.",
  },
  {
    icon: Leaf,
    label: "Seasonal Graphics",
    detail: "Four seasonal skins across mascot, banners, and camp signage.",
  },
  {
    icon: Sparkles,
    label: "Social Content Pack",
    detail: "12 platform-ready posts per month (IG / FB / TikTok).",
  },
  {
    icon: Target,
    label: "QR Updates",
    detail: "Fresh QR-linked landings for the pavilion, pool, and gate.",
  },
  {
    icon: PawPrint,
    label: "Merch Ideas",
    detail: "Quarterly capsule drops — tees, stickers, plushies.",
  },
  {
    icon: Radio,
    label: "Web Updates",
    detail: "Rolling refresh of the /bentley experience with new content.",
  },
  {
    icon: Calendar,
    label: "Holiday Campaigns",
    detail: "Themed pushes for the 6 major camping holidays.",
  },
  {
    icon: ShieldCheck,
    label: "Event Support",
    detail: "Dedicated Bentley activation for signature weekend events.",
  },
];

/* Robust clipboard — falls back to a hidden textarea + execCommand for
   mobile Safari where the async API can silently fail w/o user-gesture ctx. */
async function copyToClipboard(text) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_err) {
    /* fall through to fallback */
  }
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.top = "0";
    ta.style.left = "0";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch (_err) {
    return false;
  }
}

function buildLocalLink(shopName, city) {
  const params = new URLSearchParams();
  const n = shopName.trim();
  const c = city.trim();
  if (n) params.set("name", n);
  if (c) params.set("city", c);
  const qs = params.toString();
  return qs ? `${BASE}/local?${qs}` : `${BASE}/local`;
}

function buildDemoLink(variant, shopName, city) {
  const params = new URLSearchParams();
  const n = shopName.trim();
  const c = city.trim();
  if (n) params.set("name", n);
  if (c) params.set("city", c);
  params.set("variant", variant);
  return `${BASE}/demo?${params.toString()}`;
}

export default function Arsenal() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [unlocked, setUnlocked] = useState(false);
  const [pwInput, setPwInput] = useState("");
  const [pwError, setPwError] = useState(false);

  useEffect(() => {
    const keyParam = (searchParams.get("key") || "").trim();
    const sessionUnlocked = sessionStorage.getItem(SESSION_STORAGE_KEY) === "1";
    if (keyParam === ARSENAL_KEY) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, "1");
      setUnlocked(true);
    } else if (sessionUnlocked) {
      setUnlocked(true);
    }
  }, [searchParams]);

  const handleUnlock = (e) => {
    e?.preventDefault();
    if (pwInput.trim() === ARSENAL_KEY) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, "1");
      const next = new URLSearchParams(searchParams);
      next.set("key", ARSENAL_KEY);
      setSearchParams(next, { replace: true });
      setUnlocked(true);
      setPwError(false);
    } else {
      setPwError(true);
    }
  };

  if (!unlocked) {
    return (
      <GateScreen
        pwInput={pwInput}
        setPwInput={setPwInput}
        pwError={pwError}
        setPwError={setPwError}
        onSubmit={handleUnlock}
      />
    );
  }

  return <ArsenalConsole />;
}

/* ------------------------------------------------------------------ Gate --- */

function GateScreen({ pwInput, setPwInput, pwError, setPwError, onSubmit }) {
  return (
    <div
      className="min-h-screen bg-[#060B14] text-white flex items-center justify-center px-6"
      data-testid="arsenal-gate"
    >
      <form onSubmit={onSubmit} className="w-full max-w-sm">
        <div className="flex flex-col items-center text-center mb-8">
          <div className="w-14 h-14 rounded-sm border border-white/10 flex items-center justify-center bg-[#0A1122] mb-4">
            <Lock size={20} className="text-[#D4AF37]" />
          </div>
          <div className="text-[10px] uppercase tracking-[0.32em] text-slate-500 mb-2">
            Restricted
          </div>
          <div className="font-display text-xl tracking-tight text-white">
            Digital Arsenal
          </div>
          <div className="text-xs text-slate-500 mt-2">
            Authorized field operators only.
          </div>
        </div>

        <input
          type="password"
          value={pwInput}
          onChange={(e) => {
            setPwInput(e.target.value);
            if (pwError) setPwError(false);
          }}
          placeholder="Access key"
          autoFocus
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          data-testid="arsenal-gate-input"
          className={`w-full bg-[#0A1122] border rounded-sm px-4 py-3 text-white text-base focus:outline-none focus:border-[#D4AF37] transition-colors ${
            pwError ? "border-red-500/60" : "border-white/10"
          }`}
        />

        {pwError && (
          <div
            className="mt-3 text-[11px] uppercase tracking-[0.2em] text-red-400 text-center"
            data-testid="arsenal-gate-error"
          >
            Access denied
          </div>
        )}

        <button
          type="submit"
          data-testid="arsenal-gate-submit"
          className="btn-gold w-full mt-5 py-3 rounded-sm text-sm font-display uppercase tracking-widest inline-flex items-center justify-center gap-2"
        >
          <ShieldCheck size={14} /> Unlock
        </button>
      </form>
    </div>
  );
}

/* --------------------------------------------------------------- Console --- */

function ArsenalConsole() {
  const [shopName, setShopName] = useState("");
  const [city, setCity] = useState("");
  const [ownerName, setOwnerName] = useState("");
  const [copiedId, setCopiedId] = useState(null);
  // Creative Retainer · Bentley's Adventure Club (simulated controls).
  const [adventureLive, setAdventureLive] = useState(false);
  const [season, setSeason] = useState("summer");

  const shopNameDisplay = useMemo(
    () => (shopName.trim() ? shopName.trim() : "your shop"),
    [shopName]
  );
  const contactDisplay = useMemo(
    () => (ownerName.trim() ? ownerName.trim() : "there"),
    [ownerName]
  );

  const localLink = useMemo(
    () => buildLocalLink(shopName, city),
    [shopName, city]
  );
  const demoLinkMc = useMemo(
    () => buildDemoLink("mc", shopName, city),
    [shopName, city]
  );

  const strike1Pitch = useMemo(
    () =>
      `Hey ${shopNameDisplay}, I made you something. 60 seconds, no pitch — watch this then run your own revenue leak scan: ${localLink}`,
    [shopNameDisplay, localLink]
  );

  const strike2Pitch = useMemo(
    () =>
      `Hey ${contactDisplay}, your revenue leak report just came back — you're leaving money on the table. Here's a 60-second breakdown of what's happening and how to fix it: ${COST_SLAYER_VIDEO_URL}. See your personalized demo here: ${demoLinkMc}. — Wingman Alfred`,
    [contactDisplay, demoLinkMc]
  );

  const handleCopy = async (id, text) => {
    const ok = await copyToClipboard(text);
    if (!ok) return;
    setCopiedId(id);
    setTimeout(() => {
      setCopiedId((prev) => (prev === id ? null : prev));
    }, 1500);
  };

  const clearTargets = () => {
    setShopName("");
    setCity("");
    setOwnerName("");
  };

  return (
    <div
      className="min-h-screen bg-[#060B14] text-white pb-20"
      data-testid="arsenal-console"
    >
      <div className="max-w-lg mx-auto px-5 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-[9px] uppercase tracking-[0.32em] text-slate-500 mb-1">
              Titan · Field Console
            </div>
            <div className="font-display text-xl tracking-tight text-white">
              Digital Arsenal
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-[9px] uppercase tracking-[0.28em] text-[#D4AF37]">
            <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] light-pulse" />
            Live
          </div>
        </div>

        {/* --- Target Intelligence (drives all packet copy) --- */}
        <Section title="Target Intelligence" accent="#D4AF37">
          <div className="space-y-3">
            <input
              type="text"
              value={shopName}
              onChange={(e) => setShopName(e.target.value)}
              placeholder="Shop Name"
              data-testid="arsenal-input-shop-name"
              autoCapitalize="words"
              className="w-full bg-[#0A1122] border border-white/10 rounded-sm px-4 py-3 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4AF37]"
              maxLength={80}
            />
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="City, ST"
              data-testid="arsenal-input-city"
              autoCapitalize="words"
              className="w-full bg-[#0A1122] border border-white/10 rounded-sm px-4 py-3 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4AF37]"
              maxLength={60}
            />
            <input
              type="text"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
              placeholder="Owner First Name (optional)"
              data-testid="arsenal-input-owner"
              autoCapitalize="words"
              className="w-full bg-[#0A1122] border border-white/10 rounded-sm px-4 py-3 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-[#D4AF37]"
              maxLength={40}
            />
            <button
              type="button"
              onClick={clearTargets}
              data-testid="arsenal-clear"
              className="w-full py-2 rounded-sm text-[10px] uppercase tracking-[0.32em] text-slate-500 border border-white/5 hover:text-white hover:border-white/20 transition-colors inline-flex items-center justify-center gap-2"
            >
              <X size={12} /> Clear
            </button>
          </div>
        </Section>

        {/* ========================= STRIKE 1 ========================= */}
        <PhaseHeader
          number="1"
          title="Strike 1 — The Hook"
          subtitle="Send this FIRST. Gets them into the funnel."
          accent="#22D3EE"
          icon={<Target size={13} />}
        />

        <div
          className="rounded-sm border border-[#22D3EE]/25 bg-[#22D3EE]/[0.04] p-4 mt-4 space-y-2"
          data-testid="arsenal-strike1-primaries"
        >
          <CopyButton
            id="s1-amanda"
            label="Copy Amanda Video URL"
            onCopy={() => handleCopy("s1-amanda", AMANDA_VIDEO_URL)}
            copied={copiedId === "s1-amanda"}
            testId="arsenal-copy-s1-amanda"
            variant="cyan"
            fullWidth
          />
          <CopyButton
            id="s1-local"
            label="Copy Personalized /local Link"
            onCopy={() => handleCopy("s1-local", localLink)}
            copied={copiedId === "s1-local"}
            testId="arsenal-copy-s1-local"
            variant="cyan"
            fullWidth
          />
          <CopyButton
            id="s1-pitch"
            label="Copy Hook Pitch Text"
            onCopy={() => handleCopy("s1-pitch", strike1Pitch)}
            copied={copiedId === "s1-pitch"}
            testId="arsenal-copy-s1-pitch"
            variant="cyan"
            fullWidth
          />
        </div>

        <Section title="Strike Packets" accent="#22D3EE">
          <div className="space-y-4">
            {STRIKE_PACKETS.map((pkt) => {
              const demoLink = buildDemoLink(pkt.variant, shopName, city);
              const pitchText = pkt.pitch(shopNameDisplay, demoLink);
              const linkId = `${pkt.id}-link`;
              const pitchId = `${pkt.id}-pitch`;
              return (
                <div
                  key={pkt.id}
                  className="rounded-sm border border-white/10 bg-[#0A1122]/60 p-4"
                  data-testid={`arsenal-packet-${pkt.id}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ backgroundColor: pkt.accent }}
                    />
                    <div className="text-[10px] uppercase tracking-[0.28em] text-white">
                      {pkt.label}
                    </div>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-3">
                    {pkt.subtitle}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <CopyButton
                      id={linkId}
                      label="Copy Demo Link"
                      onCopy={() => handleCopy(linkId, demoLink)}
                      copied={copiedId === linkId}
                      testId={`arsenal-copy-${pkt.id}-link`}
                    />
                    <CopyButton
                      id={pitchId}
                      label="Copy Pitch Text"
                      onCopy={() => handleCopy(pitchId, pitchText)}
                      copied={copiedId === pitchId}
                      testId={`arsenal-copy-${pkt.id}-pitch`}
                    />
                  </div>
                </div>
              );
            })}

            {/* Mobile Sentry — 3-link field packet (Hook video · Proof demo · Guide PDF). */}
            <MobileSentryPacket
              shopName={shopName}
              city={city}
              shopNameDisplay={shopNameDisplay}
              copiedId={copiedId}
              handleCopy={handleCopy}
            />
          </div>
        </Section>

        {/* ========================= STRIKE 2 ========================= */}
        <PhaseHeader
          number="2"
          title="Strike 2 — The Proof"
          subtitle="Auto-fires 5 min after assessment. Manual override here."
          accent="#D4AF37"
          icon={<Radio size={13} />}
          badge={
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] uppercase tracking-[0.28em] bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#D4AF37]"
              data-testid="arsenal-strike2-auto-badge"
            >
              <Zap size={9} /> Auto
            </span>
          }
        />

        <div
          className="rounded-sm border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] p-4 mt-4 space-y-2"
          data-testid="arsenal-strike2-primaries"
        >
          <CopyButton
            id="s2-cost-slayer"
            label="Copy Cost Slayer Video URL"
            onCopy={() => handleCopy("s2-cost-slayer", COST_SLAYER_VIDEO_URL)}
            copied={copiedId === "s2-cost-slayer"}
            testId="arsenal-copy-s2-cost-slayer"
            fullWidth
          />
          <CopyButton
            id="s2-demo"
            label="Copy Personalized /demo Link"
            onCopy={() => handleCopy("s2-demo", demoLinkMc)}
            copied={copiedId === "s2-demo"}
            testId="arsenal-copy-s2-demo"
            fullWidth
          />
          <CopyButton
            id="s2-pitch"
            label="Copy Follow-up Pitch Text"
            onCopy={() => handleCopy("s2-pitch", strike2Pitch)}
            copied={copiedId === "s2-pitch"}
            testId="arsenal-copy-s2-pitch"
            fullWidth
          />
        </div>

        {/* Secondary video assets — Copy URL + Download side-by-side. */}
        <Section title="Video Arsenal" accent="#A78BFA">
          <div className="space-y-2">
            {VIDEO_ASSETS.map((v) => (
              <div
                key={v.id}
                className="grid grid-cols-[1fr_auto] gap-2"
                data-testid={`arsenal-video-row-${v.id}`}
              >
                <CopyButton
                  id={`v-${v.id}`}
                  label={v.label}
                  onCopy={() => handleCopy(`v-${v.id}`, v.url)}
                  copied={copiedId === `v-${v.id}`}
                  testId={`arsenal-video-${v.id}`}
                  fullWidth
                />
                <a
                  href={v.url}
                  download={v.filename}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`arsenal-video-download-${v.id}`}
                  aria-label={`Download ${v.label}`}
                  className="h-12 w-12 rounded-sm border border-[#A78BFA]/40 bg-[#A78BFA]/10 text-[#A78BFA] hover:bg-[#A78BFA]/15 hover:border-[#A78BFA]/60 inline-flex items-center justify-center transition-colors"
                >
                  <Download size={14} />
                </a>
              </div>
            ))}
          </div>
        </Section>

        {/* Bespoke Mascot · $497 add-on preview */}
        <Section title="Bespoke Mascot · Add-On" accent="#22C55E">
          <div
            className="rounded-sm border p-5"
            style={{
              borderColor: "rgba(34,197,94,0.35)",
              background:
                "linear-gradient(180deg, rgba(34,197,94,0.08) 0%, rgba(10,17,34,0.6) 100%)",
              boxShadow: "0 0 40px -18px rgba(34,197,94,0.45)",
            }}
            data-testid="arsenal-bespoke-mascot"
          >
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-sm border flex items-center justify-center"
                  style={{
                    borderColor: "rgba(34,197,94,0.55)",
                    background: "rgba(34,197,94,0.10)",
                  }}
                >
                  <PawPrint size={14} className="text-[#22C55E]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-white">
                    Bespoke Mascot
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mt-0.5">
                    Custom guest-mode character · white-label
                  </div>
                </div>
              </div>
              <div
                className="text-right leading-tight"
                data-testid="arsenal-bespoke-price"
              >
                <div className="font-display text-2xl text-[#22C55E]">$497</div>
                <div className="text-[9px] uppercase tracking-[0.24em] text-slate-500">
                  one-time
                </div>
              </div>
            </div>

            <p className="text-[13px] text-slate-300 leading-relaxed mb-3">
              Portfolio owners with a signature vibe (mascot, ranger, cartoon
              otter, glam concierge) get a custom-illustrated{" "}
              <span className="text-white">Guest Mode</span> face for their
              property — same Alfred spine, their voice on top. Live example:
              Bentley the Sentry at River Bend.
            </p>

            <ul className="space-y-1.5 mb-4">
              {[
                "1 illustrated hero portrait (customer-assets CDN)",
                "Custom greeting + name across sim, badge, and captions",
                "48-hour turnaround from brief → live in demo",
                "Portable across every /demo URL for that owner",
              ].map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-2 text-[12px] text-slate-300"
                >
                  <Check size={12} className="text-[#22C55E] mt-1 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <CopyButton
                id="bespoke-pitch"
                label="Copy Add-On Pitch"
                onCopy={() =>
                  handleCopy(
                    "bespoke-pitch",
                    "Add-on: Bespoke Mascot — $497 one-time. We illustrate a custom character for your Guest Mode (like Bentley the Sentry at River Bend). 48-hour turnaround, portable across every demo link tied to your property. Want to see Bentley live? " +
                      BASE +
                      "/demo?name=River+Bend+Family+Campground&city=Caledonia&variant=campground",
                  )
                }
                copied={copiedId === "bespoke-pitch"}
                testId="arsenal-copy-bespoke-pitch"
              />
              <CopyButton
                id="bespoke-demo"
                label="Copy Live Bentley Demo Link"
                onCopy={() =>
                  handleCopy(
                    "bespoke-demo",
                    `${BASE}/demo?name=River+Bend+Family+Campground&city=Caledonia&variant=campground`,
                  )
                }
                copied={copiedId === "bespoke-demo"}
                testId="arsenal-copy-bespoke-demo"
              />
            </div>

            <div className="mt-3 flex items-center gap-1.5 text-[9px] uppercase tracking-[0.24em] text-slate-500">
              <Sparkles size={10} className="text-[#22C55E]" />
              Signature move for multi-property owners
            </div>
          </div>
        </Section>

        {/* Creative Retainer · Bentley's Adventure Club */}
        <Section title="Creative Retainer · Adventure Club" accent="#F59E0B">
          <div
            className="rounded-sm border p-5"
            style={{
              borderColor: "rgba(245,158,11,0.35)",
              background:
                "linear-gradient(180deg, rgba(245,158,11,0.08) 0%, rgba(10,17,34,0.6) 100%)",
              boxShadow: "0 0 40px -18px rgba(245,158,11,0.45)",
            }}
            data-testid="arsenal-adventure-club"
          >
            <div className="flex items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-sm border flex items-center justify-center"
                  style={{
                    borderColor: "rgba(245,158,11,0.55)",
                    background: "rgba(245,158,11,0.10)",
                  }}
                >
                  <PawPrint size={14} className="text-[#F59E0B]" />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-white">
                    Bentley&apos;s Adventure Club
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mt-0.5">
                    Monthly creative retainer · long-term IP
                  </div>
                </div>
              </div>
              <a
                href="/brief"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="arsenal-adventure-brief-link"
                className="text-[10px] uppercase tracking-[0.24em] px-3 py-1.5 rounded-sm border transition-colors"
                style={{
                  borderColor: "rgba(245,158,11,0.45)",
                  color: "#F59E0B",
                  background: "rgba(245,158,11,0.08)",
                }}
              >
                Open 1-Page Brief ↗
              </a>
            </div>

            {/* Monthly Adventure toggle */}
            <div
              className="rounded-sm border p-3 mb-4 flex items-center justify-between gap-3"
              style={{
                borderColor: "rgba(255,255,255,0.08)",
                background: "rgba(6,11,20,0.55)",
              }}
              data-testid="adventure-toggle-row"
            >
              <div>
                <div className="text-[10px] uppercase tracking-[0.26em] text-slate-300">
                  Monthly Adventure
                </div>
                <div className="text-[11px] text-slate-500 mt-1">
                  {adventureLive
                    ? `Pushed to /bentley · ${SEASON_CONFIG[season].story}`
                    : "Draft ready. Toggle to simulate push to /bentley."}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setAdventureLive((v) => !v)}
                data-testid="adventure-toggle"
                aria-pressed={adventureLive}
                className="relative h-7 w-14 rounded-full transition-colors"
                style={{
                  background: adventureLive ? "#F59E0B" : "rgba(255,255,255,0.10)",
                  border: `1px solid ${
                    adventureLive ? "#F59E0B" : "rgba(255,255,255,0.20)"
                  }`,
                }}
              >
                <span
                  className="absolute top-0.5 h-5 w-5 rounded-full transition-transform"
                  style={{
                    background: "#FFF7ED",
                    transform: adventureLive ? "translateX(30px)" : "translateX(2px)",
                    boxShadow: "0 4px 10px -2px rgba(0,0,0,0.4)",
                  }}
                />
              </button>
            </div>

            {adventureLive && (
              <div
                className="rounded-sm px-3 py-2 mb-4 text-[10px] uppercase tracking-[0.22em] inline-flex items-center gap-1.5"
                style={{
                  background: "rgba(34,197,94,0.12)",
                  border: "1px solid rgba(34,197,94,0.45)",
                  color: "#22C55E",
                }}
                data-testid="adventure-live-badge"
              >
                <Check size={11} /> Simulated Push · Live on /bentley
              </div>
            )}

            {/* Seasonal Camouflage selector */}
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-[0.26em] text-slate-400 mb-2">
                Seasonal Camouflage
              </div>
              <div
                className="grid grid-cols-4 gap-2"
                data-testid="season-selector"
                role="radiogroup"
                aria-label="Seasonal camouflage"
              >
                {SEASON_ORDER.map((k) => {
                  const s = SEASON_CONFIG[k];
                  const Icon = s.icon;
                  const active = season === k;
                  return (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setSeason(k)}
                      role="radio"
                      aria-checked={active}
                      data-testid={`season-${k}`}
                      className="py-2.5 rounded-sm border text-[10px] uppercase tracking-[0.24em] inline-flex flex-col items-center gap-1 transition-colors"
                      style={{
                        borderColor: active ? s.accent : "rgba(255,255,255,0.10)",
                        background: active ? `${s.accent}18` : "transparent",
                        color: active ? s.accent : "#94A3B8",
                        fontWeight: active ? 700 : 400,
                      }}
                    >
                      <Icon size={13} />
                      {s.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Season preview card */}
            <div
              className="rounded-sm border p-3 space-y-1.5"
              style={{
                borderColor: `${SEASON_CONFIG[season].accent}44`,
                background: `${SEASON_CONFIG[season].accent}0A`,
              }}
              data-testid={`season-preview-${season}`}
            >
              {[
                ["Palette", SEASON_CONFIG[season].palette],
                ["Story Beat", SEASON_CONFIG[season].story],
                ["Graphic Motif", SEASON_CONFIG[season].graphic],
                ["Campaign Push", SEASON_CONFIG[season].campaign],
              ].map(([k, v]) => (
                <div key={k} className="flex items-start gap-3 text-[12px]">
                  <div
                    className="text-[9px] uppercase tracking-[0.22em] w-24 shrink-0 pt-0.5"
                    style={{ color: SEASON_CONFIG[season].accent }}
                  >
                    {k}
                  </div>
                  <div className="text-slate-200">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </Section>

        {/* Hook Vault */}
        <Section title="Hook Vault" accent="#F97316">
          <div className="space-y-3">
            {HOOKS.map((h) => (
              <div
                key={h.id}
                className="rounded-sm border border-white/10 bg-[#0A1122]/60 p-3 flex items-start gap-3"
                data-testid={`arsenal-hook-${h.id}`}
              >
                <div className="flex-1 text-sm text-slate-200 leading-relaxed">
                  {h.text}
                </div>
                <button
                  type="button"
                  onClick={() => handleCopy(h.id, h.text)}
                  data-testid={`arsenal-copy-${h.id}`}
                  className={`shrink-0 min-w-[68px] h-12 px-3 rounded-sm text-[10px] uppercase tracking-[0.24em] inline-flex items-center justify-center gap-1.5 transition-colors ${
                    copiedId === h.id
                      ? "bg-green-500/15 border border-green-400/40 text-green-300"
                      : "bg-white/5 border border-white/10 text-slate-300 hover:text-white hover:border-white/20"
                  }`}
                >
                  {copiedId === h.id ? (
                    <>
                      <Check size={12} /> Copied
                    </>
                  ) : (
                    <>
                      <Copy size={12} /> Copy
                    </>
                  )}
                </button>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- Primitives --- */

/* ---------------------------------------------------- Mobile Sentry Packet
   Field-technician preset: three-link high-velocity packet John can copy
   in ~5 seconds per prospect. Uses a distinct "iron red" accent so it
   never gets confused with the MC / Auto / Suit / Campground packets. */

const MOBILE_SENTRY_ACCENT = "#DC2626";
const AMANDA_MP4 =
  "https://customer-assets.emergentagent.com/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/9da22c6dcc8147a8a43e6703782751af_amanda_commercial_final.mp4";
const GUIDE_LINK = `${BASE}/guide`;

function MobileSentryPacket({ shopName, city, shopNameDisplay, copiedId, handleCopy }) {
  const demoLink = buildDemoLink("work", shopName, city);
  const pitch =
    `Hey ${shopNameDisplay}, built for operators in the field. Don't answer calls with greasy hands. ` +
    `Let Alfred run the front door while you run the wrench. ` +
    `Watch: ${AMANDA_MP4}. See what your shop would look like: ${demoLink}. Read the guide: ${GUIDE_LINK}`;

  return (
    <div
      className="rounded-sm border p-4"
      style={{
        borderColor: `${MOBILE_SENTRY_ACCENT}55`,
        background:
          `linear-gradient(180deg, ${MOBILE_SENTRY_ACCENT}0F 0%, rgba(10,17,34,0.6) 100%)`,
      }}
      data-testid="arsenal-packet-mobile-sentry"
    >
      <div className="flex items-center gap-2 mb-3">
        <span
          className="w-1.5 h-1.5 rounded-full"
          style={{ backgroundColor: MOBILE_SENTRY_ACCENT }}
        />
        <div className="text-[10px] uppercase tracking-[0.28em] text-white">
          Mobile Sentry
        </div>
        <span
          className="ml-auto px-1.5 py-0.5 rounded-sm text-[8px] uppercase tracking-[0.24em]"
          style={{
            background: `${MOBILE_SENTRY_ACCENT}18`,
            border: `1px solid ${MOBILE_SENTRY_ACCENT}55`,
            color: MOBILE_SENTRY_ACCENT,
          }}
        >
          3-Link Field Preset
        </span>
      </div>
      <div className="text-[10px] uppercase tracking-[0.24em] text-slate-500 mb-3">
        Mobile Mechanic · Trades on the Truck
      </div>

      <p
        className="text-[13px] leading-relaxed mb-4 italic"
        style={{ color: "#F1F5F9" }}
        data-testid="arsenal-mobile-sentry-narrative"
      >
        &ldquo;Built for operators in the field. Don&apos;t answer calls with
        greasy hands. Let Alfred run the front door while you run the
        wrench.&rdquo;
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <CopyButton
          id="mobile-sentry-hook"
          label="1 · Hook (Amanda Video)"
          onCopy={() => handleCopy("mobile-sentry-hook", AMANDA_MP4)}
          copied={copiedId === "mobile-sentry-hook"}
          testId="arsenal-copy-mobile-sentry-hook"
        />
        <CopyButton
          id="mobile-sentry-proof"
          label="2 · Proof (Demo)"
          onCopy={() => handleCopy("mobile-sentry-proof", demoLink)}
          copied={copiedId === "mobile-sentry-proof"}
          testId="arsenal-copy-mobile-sentry-proof"
        />
        <CopyButton
          id="mobile-sentry-guide"
          label="3 · Guide (PDF)"
          onCopy={() => handleCopy("mobile-sentry-guide", GUIDE_LINK)}
          copied={copiedId === "mobile-sentry-guide"}
          testId="arsenal-copy-mobile-sentry-guide"
        />
      </div>

      <div className="mt-2">
        <CopyButton
          id="mobile-sentry-pitch"
          label="Copy Full 3-Link Pitch"
          onCopy={() => handleCopy("mobile-sentry-pitch", pitch)}
          copied={copiedId === "mobile-sentry-pitch"}
          testId="arsenal-copy-mobile-sentry-pitch"
          fullWidth
        />
      </div>
    </div>
  );
}


function PhaseHeader({ number, title, subtitle, accent, icon, badge }) {
  return (
    <div
      className="mt-10 rounded-sm border p-4"
      style={{
        borderColor: `${accent}55`,
        background: `linear-gradient(180deg, ${accent}14 0%, transparent 100%)`,
      }}
      data-testid={`arsenal-phase-header-${number}`}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-sm border flex items-center justify-center text-[11px] font-display"
            style={{ borderColor: `${accent}80`, color: accent, background: `${accent}12` }}
          >
            {number}
          </div>
          <div>
            <div
              className="flex items-center gap-2 text-[13px] font-display tracking-tight"
              style={{ color: accent }}
            >
              {icon}
              <span>{title}</span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mt-1">
              {subtitle}
            </div>
          </div>
        </div>
        {badge}
      </div>
    </div>
  );
}

function Section({ title, accent, children }) {
  return (
    <section className="mt-8 first:mt-4">
      <div className="flex items-center gap-3 mb-4">
        <span
          className="h-px flex-1"
          style={{
            background: `linear-gradient(90deg, ${accent}55, transparent)`,
          }}
        />
        <div className="text-[10px] uppercase tracking-[0.32em] text-slate-400">
          {title}
        </div>
        <span
          className="h-px flex-1"
          style={{
            background: `linear-gradient(90deg, transparent, ${accent}55)`,
          }}
        />
      </div>
      {children}
    </section>
  );
}

function CopyButton({ id, label, onCopy, copied, testId, fullWidth = false, variant = "gold" }) {
  const palette =
    variant === "cyan"
      ? {
          bg: "bg-[#22D3EE]/10",
          border: "border-[#22D3EE]/40",
          text: "text-[#22D3EE]",
          hover: "hover:bg-[#22D3EE]/15 hover:border-[#22D3EE]/60",
        }
      : {
          bg: "bg-[#D4AF37]/10",
          border: "border-[#D4AF37]/40",
          text: "text-[#D4AF37]",
          hover: "hover:bg-[#D4AF37]/15 hover:border-[#D4AF37]/60",
        };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testId || `arsenal-copy-${id}`}
      className={`h-12 px-4 rounded-sm text-[11px] uppercase tracking-[0.26em] inline-flex items-center justify-center gap-2 transition-colors ${
        copied
          ? "bg-green-500/15 border border-green-400/50 text-green-300"
          : `${palette.bg} border ${palette.border} ${palette.text} ${palette.hover}`
      } ${fullWidth ? "w-full" : ""}`}
    >
      {copied ? (
        <>
          <Check size={13} /> Copied
        </>
      ) : (
        <>
          <Copy size={13} /> {label}
        </>
      )}
    </button>
  );
}
